/**
 * KeyResultsSearchResults
 *
 * Displays a scrollable list of key results, including title, linked counts, and
 * a status-colored circular progress ring. Supports two modes:
 * - Default: clicking navigates to the Company Dashboard (Objectives tab) with
 *   objectiveId (first if multiple) and keyresultId set in the query string.
 * - selectMode: clicking invokes onSelect(title) instead of navigation.
 *
 * Additional behaviors
 * - If a key result is unlinked (objectiveId list empty), renders semi-transparent
 *   visuals and shows tooltips/actions to re-link or remove (permission-gated).
 * - Uses OverlayScrollbars for consistent, themeable scrollbars.
 *
 * Props
 * - results: Key result items to render
 * - onSelect?: (title: string) => void; used when selectMode=true
 * - selectMode?: when true, suppress routing and call onSelect instead
 * - setSelectedKeyResultId: used to trigger re-link flow in parent
 * - setEditKeyResultsPopupOpen: toggles edit popup in parent
 * - setLoading: toggles loading in parent during async operations
 * - refreshData: refetches the list after mutations (e.g., delete)
 */
import React from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import "./key-results-search.scss";
import { getProgressColor } from "../../../../features/company-dashboard/company-dashboard-utils";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { routes } from "../../../../constants/routes";

import linkBreakIcon from "../../../../assets/icons/link.svg";
import deleteRedIcon from "../../../../assets/icons/delete-red.svg";
import { deleteKeyResult } from "../../../../api/objectives";
import MODULES from "../../../../constants/modules";
import { usePermissions } from "../../../hooks/usePermissions";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

interface Objective {
  id: number;
  title: string;
  objectiveCount: number;
  initiativeCount: number;
  progress: number;
  objectiveId: any;
}

interface Props {
  results: Objective[];
  onSelect?: (keyResult: string) => void;
  selectMode?: boolean;
  setSelectedKeyResultId: (id: number | null) => void;
  setEditKeyResultsPopupOpen: (open: boolean) => void;
  setLoading: (loading: boolean) => void;
  refreshData: () => void;
}

const KeyResultsSearchResults: React.FC<Props> = ({
  results,
  onSelect,
  selectMode = false,
  setLoading,
  refreshData,
  setSelectedKeyResultId,
  setEditKeyResultsPopupOpen,
}) => {
  const navigate = useNavigate();

  const { canWrite, canDelete } = usePermissions(MODULES.KEY_RESULTS);

  const handleReLink = (keyResultId: number) => {
    setSelectedKeyResultId(keyResultId);
    setEditKeyResultsPopupOpen(true);
  };

  const handleRemove = async (keyResultId: number) => {
    setLoading(true);
    try {
      await deleteKeyResult(keyResultId);
      setLoading(false);
      refreshData();
    } catch {
      setLoading(false);
      refreshData();
    }
  };

  if (results.length === 0) {
    return <div className="no-results">No key results found.</div>;
  }

  return (
    <OverlayScrollbarsComponent
      className="objectives-search-results scrollable"
      options={{
        scrollbars: {
          theme: "os-theme-dark",
          autoHide: "scroll",
          autoHideDelay: 400,
        },
      }}
    >
      {results.map((keyres: any) => {
        const { mainColor, lightColor } = getProgressColor(keyres.status);
        const card = (
          <Box
            key={keyres.id}
            className="objective-card"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              if (keyres?.objectiveId?.length) {
                if (selectMode && onSelect) {
                  onSelect(keyres.title);
                } else {
                  const queryParams = new URLSearchParams();
                  const objectiveId = Array.isArray(keyres.objectiveId)
                    ? keyres.objectiveId[0]
                    : keyres.objectiveId;
                  queryParams.set("dashboardTab", "Objectives");
                  queryParams.set("objectiveId", String(objectiveId));
                  queryParams.set("keyresultId", String(keyres.id));
                  navigate(
                    `${routes.COMPANYDASHBOARD}?${queryParams.toString()}`,
                  );
                }
              }
            }}
          >
            {/* Left Section */}
            <Box className="key-results-meta-left">
              <Typography
                variant="subtitle1"
                sx={{ opacity: keyres?.objectiveId?.length === 0 ? 0.4 : "1" }}
              >
                {keyres.title}
              </Typography>
              <Box className="key-results-meta">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      opacity: keyres?.objectiveId?.length === 0 ? 0.4 : "1",
                    }}
                  >
                    Objectives <strong>{keyres.objectiveCount}</strong>
                  </span>
                  <Divider
                    orientation="vertical"
                    flexItem
                    style={{ marginRight: "8px" }}
                  />
                  <span>
                    Initiatives <strong>{keyres.initiativeCount}</strong>
                  </span>
                </div>
                {keyres?.objectiveId?.length === 0 ? (
                  <div>
                    <Tooltip title={"Link item to reactivate"}>
                      {canWrite ? (
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReLink(keyres.id);
                          }}
                        >
                          <img
                            alt="link Break"
                            src={linkBreakIcon}
                            className="link-icon"
                          />
                        </button>
                      ) : (
                        <></>
                      )}
                    </Tooltip>
                    <Tooltip title={"Remove Item"}>
                      {canDelete ? (
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemove(keyres.id);
                          }}
                        >
                          <img
                            alt="Delete icon"
                            src={deleteRedIcon}
                            className="close-icon"
                          />
                        </button>
                      ) : (
                        <></>
                      )}
                    </Tooltip>
                  </div>
                ) : null}
              </Box>
            </Box>

            {/* Right Section */}
            <Box
              className="progress-wrapper"
              sx={{ opacity: keyres?.objectiveId?.length === 0 ? 0.4 : "1" }}
            >
              {/* Background track (lighter shade of main color) */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={60}
                thickness={5}
                sx={{ color: lightColor, position: "absolute" }}
              />
              {/* Foreground progress */}
              <CircularProgress
                variant="determinate"
                value={keyres.progress}
                size={60}
                thickness={5}
                sx={{
                  color: mainColor,
                  "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                  },
                }}
              />
              <Box className="progress-value">
                {Math.round(keyres.progress || 0)}%
              </Box>
            </Box>
          </Box>
        );
        return keyres?.objectiveId?.length === 0 ? (
          <Tooltip
            key={keyres.id}
            title="This key result isn't linked to any objective."
            placement="top"
          >
            {card}
          </Tooltip>
        ) : (
          card
        );
      })}
    </OverlayScrollbarsComponent>
  );
};

export default KeyResultsSearchResults;
